/**
 * Dead-stock report — `/inventory/reports/dead-stock`.
 *
 * `days` is a REQUIRED query parameter: the screen keeps a valid threshold at
 * all times and never issues a request without one. Buckets, the as-of time
 * and the applied threshold come from the response, so the client never
 * invents a boundary the server did not publish.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";
import { Download } from "lucide-react";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { formatDateTime, formatNumber } from "@/lib/format";

import {
  REPORT_GROUP_BYS,
  deadStockExportHref,
  type DeadStockItem,
  type ReportGroupBy,
} from "../api";
import { useDeadStockReport, useWarehouseOptions } from "../queries";
import { AmountText, QuantityText, RatioText } from "../components/decimal-text";
import { ALL, CategorySelect } from "../components/pickers";

const DAY_PRESETS = [30, 60, 90, 180, 365] as const;
const MIN_DAYS = 1;
const MAX_DAYS = 3650;

/** Sort fields the endpoint documents; anything else is not offered. */
const SORTABLE = new Set(["days_since_last_issue", "stock_value", "on_hand_qty", "sku"]);

function warehouseCodeOf(item: DeadStockItem): string | undefined {
  return "warehouse_code" in item ? item.warehouse_code : undefined;
}

export interface DeadStockPageProps {
  canExport?: boolean;
  canReadCategories?: boolean;
  pageLimit?: number;
}

export function DeadStockPage({
  canExport = false,
  canReadCategories = false,
  pageLimit,
}: DeadStockPageProps) {
  const { t, i18n } = useTranslation();

  const [daysInput, setDaysInput] = useState("90");
  const [groupBy, setGroupBy] = useState<ReportGroupBy>("warehouse");
  const [warehouseId, setWarehouseId] = useState<string>(ALL);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: "stock_value", dir: "desc" });

  const warehouses = useWarehouseOptions().data ?? [];

  const parsedDays = Number.parseInt(daysInput, 10);
  const daysValid =
    Number.isInteger(parsedDays) && parsedDays >= MIN_DAYS && parsedDays <= MAX_DAYS;

  const params = useMemo(
    () => ({
      days: daysValid ? parsedDays : MIN_DAYS,
      group_by: groupBy,
      ...(warehouseId === ALL ? {} : { warehouse_id: warehouseId }),
      ...(categoryId === null ? {} : { category_id: categoryId }),
      ...(sort === null ? {} : { sort }),
      ...(pageLimit === undefined ? {} : { limit: pageLimit }),
    }),
    [daysValid, parsedDays, groupBy, warehouseId, categoryId, sort, pageLimit],
  );

  const report = useDeadStockReport(params, daysValid);
  const meta = report.meta;

  const columns = useMemo<ColumnDef<DeadStockItem>[]>(
    () => [
      {
        accessorKey: "sku",
        id: "sku",
        header: t("inventory.common.sku", "SKU"),
        enableSorting: SORTABLE.has("sku"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        id: "warehouse",
        header: t("inventory.common.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseCodeOf(ctx.row.original) ?? ctx.row.original.branch_code),
      },
      {
        accessorKey: "on_hand_qty",
        id: "on_hand_qty",
        header: t("inventory.stock.columns.onHand", "On hand"),
        enableSorting: SORTABLE.has("on_hand_qty"),
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "average_unit_value",
        id: "average_unit_value",
        header: t("inventory.stock.columns.avgUnitValue", "Avg unit value"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <RatioText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "stock_value",
        id: "stock_value",
        header: t("inventory.common.value", "Value"),
        enableSorting: SORTABLE.has("stock_value"),
        meta: { numeric: true },
        cell: (ctx) => <AmountText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "last_issue_at",
        id: "last_issue_at",
        header: t("inventory.deadStock.lastIssue", "Last issued"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | null>();
          if (value === null) {
            return (
              <Badge variant="warning">
                {t("inventory.deadStock.neverIssued", "Never issued")}
              </Badge>
            );
          }
          return dateTimeCell(value);
        },
      },
      {
        accessorKey: "days_since_last_issue",
        id: "days_since_last_issue",
        header: t("inventory.deadStock.daysSince", "Days idle"),
        enableSorting: SORTABLE.has("days_since_last_issue"),
        meta: { numeric: true },
        // A whole-day count, not money or a wire quantity: a JS number is exact here.
        cell: (ctx) => {
          const value = ctx.getValue<number | undefined>();
          return value === undefined ? textCell(undefined) : formatNumber(value, i18n.language);
        },
      },
      {
        accessorKey: "bucket_code",
        id: "bucket_code",
        header: t("inventory.deadStock.bucket", "Bucket"),
        enableSorting: false,
        cell: (ctx) => badgeCell(ctx.getValue<string>(), {}, ctx.getValue<string>()),
      },
    ],
    [t, i18n.language],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("inventory.deadStock.title", "Dead stock")}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.deadStock.subtitle",
              "Positions still on hand that nothing has issued for the chosen number of days.",
            )}
          </p>
        </div>
        {canExport && daysValid ? (
          <Button asChild variant="outline">
            <a
              href={deadStockExportHref({
                days: parsedDays,
                group_by: groupBy,
                ...(warehouseId === ALL ? {} : { warehouse_id: warehouseId }),
                ...(categoryId === null ? {} : { category_id: categoryId }),
              })}
              target="_blank"
              rel="noreferrer"
            >
              <Download className="size-4" aria-hidden="true" />
              {t("inventory.deadStock.export", "Export CSV")}
            </a>
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dead-stock-days">
            {t("inventory.deadStock.days", "Idle for at least (days)")}
          </Label>
          <Input
            id="dead-stock-days"
            dir="ltr"
            inputMode="numeric"
            value={daysInput}
            aria-invalid={!daysValid}
            onChange={(event) => setDaysInput(event.target.value)}
          />
          {daysValid ? (
            <div className="flex flex-wrap gap-1">
              {DAY_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  size="sm"
                  variant={parsedDays === preset ? "default" : "outline"}
                  className="h-7 px-2 text-xs"
                  onClick={() => setDaysInput(String(preset))}
                >
                  {formatNumber(preset, i18n.language)}
                </Button>
              ))}
            </div>
          ) : (
            <p role="alert" className="text-sm text-destructive">
              {t(
                "inventory.deadStock.daysInvalid",
                "Enter a whole number of days between 1 and 3650 — this report cannot run without it.",
              )}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dead-stock-group">{t("inventory.reports.groupBy", "Grouped by")}</Label>
          <Select value={groupBy} onValueChange={(value) => setGroupBy(value as ReportGroupBy)}>
            <SelectTrigger id="dead-stock-group">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_GROUP_BYS.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.reports.group.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dead-stock-warehouse">
            {t("inventory.common.warehouse", "Warehouse")}
          </Label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger id="dead-stock-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadCategories ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dead-stock-category">
              {t("inventory.common.category", "Category")}
            </Label>
            <CategorySelect
              id="dead-stock-category"
              value={categoryId}
              canRead={canReadCategories}
              onChange={setCategoryId}
            />
          </div>
        ) : null}
      </div>

      {meta !== null ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-xs">
          <span className="text-muted-foreground">
            {t("inventory.reports.asOf", "As of {{when}}", {
              when: formatDateTime(meta.as_of, i18n.language),
            })}
          </span>
          {meta.threshold_days !== undefined ? (
            <Badge variant="outline">
              {t("inventory.deadStock.threshold", "{{days}} day threshold", {
                days: meta.threshold_days,
              })}
            </Badge>
          ) : null}
          {meta.bucket_definitions.map((bucket) => (
            <Badge key={bucket.code} variant="secondary">
              {bucket.code}
              {" · "}
              {bucket.maximum_age_days === null
                ? t("inventory.reports.bucketOpen", "{{min}}+ days", {
                    min: bucket.minimum_age_days,
                  })
                : t("inventory.reports.bucketRange", "{{min}}–{{max}} days", {
                    min: bucket.minimum_age_days,
                    max: bucket.maximum_age_days,
                  })}
            </Badge>
          ))}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={report.table}
        getRowId={(row) =>
          `${row.variant_id}:${warehouseCodeOf(row) ?? row.branch_id}:${row.bucket_code}`
        }
        sort={sort}
        onSortChange={setSort}
        storageKey="inventory.reports.dead-stock.columns"
        emptyMessage={t(
          "inventory.deadStock.empty",
          "Nothing is idle at these parameters — every position has moved.",
        )}
        errorMessage={t("inventory.deadStock.loadError", "The report could not be loaded.")}
      />
    </div>
  );
}
